import { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import api from '../config/api';

export default function LedgerReports() {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [isLoading, setIsLoading] = useState(false);
  const [accountCodes, setAccountCodes] = useState([]);

  useEffect(() => {
    const fetchAccountCodes = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(api("/account_codes/"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        setAccountCodes(data);
      } catch (err) {
        console.error("Failed to fetch account codes:", err);
      }
    };
    fetchAccountCodes();
  }, []);

  const isLeapYear = (year) =>
    (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

  const getMonths = (year) => [
    { name: "January", start: "01", end: "31" },
    { name: "February", start: "02", end: isLeapYear(year) ? "29" : "28" },
    { name: "March", start: "03", end: "31" },
    { name: "April", start: "04", end: "30" },
    { name: "May", start: "05", end: "31" },
    { name: "June", start: "06", end: "30" },
    { name: "July", start: "07", end: "31" },
    { name: "August", start: "08", end: "31" },
    { name: "September", start: "09", end: "30" },
    { name: "October", start: "10", end: "31" },
    { name: "November", start: "11", end: "30" },
    { name: "December", start: "12", end: "31" },
  ];

  const fetchTransactionsByMonth = async (month) => {
    setIsLoading(true);
    try {
      const startDate = `${selectedYear}-${month.start}-01`;
      const endDate = `${selectedYear}-${month.start}-${month.end}`;
      const token = localStorage.getItem("token");

      const response = await fetch(
        api(`/transactions/by-date?start=${startDate}&end=${endDate}`),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch transactions.");
      const data = await response.json();
      if (!data || data.length === 0) {
        alert(`Tidak ada transaksi pada bulan ${month.name} ${selectedYear}`);
        return;
      }

      exportToXLSX(data, month.name);
    } catch (err) {
      console.error("Error:", err);
      alert("Gagal mengambil data transaksi atau export.");
    } finally {
      setIsLoading(false);
    }
  };

  const exportToXLSX = async (transactions, monthName) => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Ledger");

    // Header row
    const headers = ["TANGGAL", "COA", "ACCOUNT", "KETERANGAN", "DEBIT", "KREDIT"];
    sheet.addRow(headers);
    sheet.getRow(1).eachCell((cell) => {
      cell.border = {
        top:    { style: 'thin' },
        left:   { style: 'thin' },
        bottom: { style: 'thin' },
        right:  { style: 'thin' }
      };

      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "D9E1F2" },
      };
      cell.font = { bold: true };
      cell.alignment = { vertical: "middle", horizontal: "center" };
    });

    sheet.columns = [
      { width: 12 }, // TANGGAL
      { width: 12 }, // COA
      { width: 25 }, // ACCOUNT
      { width: 30 }, // KETERANGAN
      { width: 15 }, // DEBIT
      { width: 15 }, // KREDIT
    ];

    const sorted = [...transactions].sort((a, b) => {
      if (a.account_code < b.account_code) return -1;
      if (a.account_code > b.account_code) return 1;
      return new Date(a.transaction_date) - new Date(b.transaction_date);
    });

    const groups = {};
    sorted.forEach((tx) => {
      if (!groups[tx.account_code]) groups[tx.account_code] = [];
      groups[tx.account_code].push(tx);
    });

    Object.keys(groups).forEach((code) => {
      const group = groups[code];
      const name = accountCodes.find((ac) => ac.code === code)?.name || "-";
      const debit = group.filter((t) => t.transaction_type === "debit").reduce((a, b) => a + b.amount, 0);
      const credit = group.filter((t) => t.transaction_type === "credit").reduce((a, b) => a + b.amount, 0);

      const totalRow = sheet.addRow([code, name, "Total", "", debit || "-", credit || "-"]);
      totalRow.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFD966" },
        };
        cell.font = { bold: true };
      });

      group.forEach((tx) => {
        const row = sheet.addRow([
          new Date(tx.transaction_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }).replace(" ", "-"),
          tx.account_code,
          name,
          tx.description || "-",
          tx.transaction_type === "debit" ? tx.amount : "",
          tx.transaction_type === "credit" ? tx.amount : "",
        ]);
        row.eachCell(cell => {
          cell.border = {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
          };
        });
      });
    });

    sheet.columns[4].numFmt = '"Rp" #,##0';
    sheet.columns[5].numFmt = '"Rp" #,##0';

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `ledger_${monthName.toLowerCase()}_${selectedYear}.xlsx`);
  };

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <Sidebar />
        <div className="layout-page">
          <Navbar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <h2 className="mb-4">Ledger Report</h2>
              <div className="card mb-4">
                <div className="card-body">
                  <div className="row mb-4">
                    <div className="col-md-4">
                      <label className="form-label">Select Year:</label>
                      <select
                        className="form-select"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                      >
                        {[currentYear - 1, currentYear, currentYear + 1].map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row">
                    {getMonths(selectedYear).map((month) => (
                      <div className="col-md-3 mb-3" key={month.name}>
                        <button
                          className="btn btn-primary w-100"
                          onClick={() => fetchTransactionsByMonth(month)}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            month.name
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
